/**
 * 📥 Candidate requests — inbound candidate questions with a drafted reply.
 *
 * Unlike the other tabs this one is per-MAILBOX, not per-role, so it owns its own data fetch and
 * renders a flat oldest-first list rather than a role accordion.
 *
 * It only renders on the signed-in user's own board. The proxy derives the mailbox from the
 * Firebase token and ignores any email the client sends, so a colleague's board could never show
 * their mail — but showing an empty tab there would look broken, hence the explicit explainer.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { Accordion } from '@finn-auto/design-system/atoms/accordion';
import { Button } from '@finn-auto/design-system/atoms/button';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taActionBoardApi';
import { logAnalyticsEvent, logAnalyticsError } from '../../../../analytics/nativeAnalytics';

import type { InboundRequest, InboundResponse } from '../../types/taActionBoard';
import { InboundCard } from './InboundCard';
import { COLORS } from './format';
import {
  GmailConsentGate,
  GmailExpiredBanner,
  LeverMatchBanner,
  OtherBoardNotice,
  RefreshFailedBanner,
  RequestsLoadFailed,
  RequestsLoading,
  RequestsUnavailable,
} from './RequestsStatus';
import { RoleAccordion } from './RoleAccordion';
import { readRequestsCache, updateRequestsCache, writeRequestsCache } from './requestsCache';
import type { PendingAction } from './usePendingActions';
import { loadRequestThread, replyAction } from './replyAction';
import { useCancelInterview } from './cancelInterviewState';
import { useRequestFilters } from './useRequestFilters';
import { RequestFilterBar } from './RequestFilterBar';
import { RemindersStrip } from './RemindersStrip';
import { useReminderFocus } from './useReminderFocus';
import { groupByRole, inBucket, needsAnswer } from './requestGroups';
import { ScheduledSendsStrip } from './ScheduledSendsStrip';
import { useDecoratedRequests } from './useDecoratedRequests';
import { dismissOutcome } from './dismissOutcome';
import {
  RequestSchedulingConnection,
  type SchedulingConnectionCtx,
} from './RequestSchedulingConnection';
import { useGmailConnection } from './GmailConnectionContext';

export function RequestsTab({
  isOwnBoard,
  boardFirstName,
  myEmail,
  onCountChange,
  deferAction,
  schedulingCtx,
}: {
  isOwnBoard: boolean;
  boardFirstName: string;
  myEmail: string;
  onCountChange: (n: number | null) => void;
  /**
   * The board's shared 10-second countdown (`usePendingActions`), lifted in
   * TAActionBoardView so one banner covers every deferred action. A reply is not
   * sent until it expires, which is what makes "Stop" a real undo.
   */
  deferAction: (action: Omit<PendingAction, 'secondsLeft'>) => void;
  /**
   * The board payload + modal routes behind the per-request Scheduling-tab connection. Absent
   * while the board is still loading — the cards then simply render no connection.
   */
  schedulingCtx?: SchedulingConnectionCtx;
}) {
  const gmail = useGmailConnection(); // board-wide: a connect made anywhere serves this tab too
  /**
   * Seeded from the session cache, so coming back to the tab shows the queue immediately while the
   * refresh runs underneath it. Empty on the first visit of the session — then, and only then, is
   * there a spinner.
   */
  const [items, setItems] = useState<InboundRequest[]>(() => readRequestsCache(myEmail) || []);
  // True from the first render when the page already holds a Gmail grant, so re-entering the tab
  // shows a spinner rather than a flash of "nothing waiting" before the refetch lands. Not when
  // there is something cached to show: a spinner over an answer is a step backwards.
  const [loading, setLoading] = useState(Boolean(gmail.token) && items.length === 0);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  /**
   * A SET, not one slot: two sends can be in flight (each has its own countdown),
   * and a single slot let finishing one re-enable the other's Send button — which
   * sent that candidate the same reply twice.
   */
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(() => new Set());
  const setBusy = (threadId: string, busy: boolean) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(threadId);
      else next.delete(threadId);
      return next;
    });
  /** Token lapsed mid-session: keep everything on screen and offer a one-click re-grant. */
  const [expired, setExpired] = useState(false);
  // Filter + ordering come from the URL — see useRequestFilters for why.
  const { bucket, sort, setSort, toggleBucket } = useRequestFilters();
  // Queue + scheduled join + every chip count, in one place — see useDecoratedRequests.
  // The other counts (needs you / ready / scheduled / no reply) are still computed by the hook for
  // the role accordion; the bar itself shows only Unread and the whole queue since Sep 10 2026.
  const { decorated, unreadCount, reloadScheduled } = useDecoratedRequests(items);
  const { cancelStates, queueCancelInterview } = useCancelInterview(deferAction);
  const cancelHandlerFor = (item: InboundRequest) => {
    const ref = item.scheduling?.bookingRef;
    return ref ? () => queueCancelInterview(item, ref) : undefined;
  };

  /** Which role groups are expanded; the first group opens by default, as in FeedbackTab. */
  const [openRoles, setOpenRoles] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const focusFromReminder = useReminderFocus({ items, setOpenRoles, setExpanded, setQuery });
  const [leverMatch, setLeverMatch] = useState<InboundResponse['leverMatch']>(undefined);

  /** What caused the next inbox read — stamped on `ta_requests_loaded`, then reset. */
  const loadTriggerRef = useRef<'mount' | 'manual'>('mount');
  const applyResponse = useCallback(
    (res: InboundResponse, isSearch: boolean) => {
      setLeverMatch(res.leverMatch);
      if (res.available) {
        setItems(res.items);
        setUnavailable(null);
        onCountChange(res.items.length);
        // Only the QUEUE is worth caching. Search results are newest-first and reach into the
        // archive, so seeding the tab with them would show a different list than the tab promises.
        if (!isSearch) writeRequestsCache(myEmail, res.items);
        // Counts and flags only — item contents are candidate data and stay out of analytics.
        void logAnalyticsEvent('ta_requests_loaded', {
          count: res.items.length,
          drafted: res.items.filter((i) => i.draft).length,
          demo: Boolean(res.demo),
          trigger: isSearch ? 'search' : loadTriggerRef.current,
        });
        loadTriggerRef.current = 'mount';
        // Open the role that has been waiting longest, so the tab is useful without a click.
        const first = [...res.items].sort((a, b) => b.ageDays - a.ageDays)[0];
        if (first) setOpenRoles([first.postingId || 'no-role']);
      } else {
        setItems([]);
        setUnavailable(res.reason || 'Your mailbox is not readable right now.');
        // Unknown, not zero — the badge must not claim an empty inbox we never read.
        onCountChange(null);
      }
    },
    [onCountChange, myEmail]
  );

  /**
   * Monotonic sequence guarding against out-of-order responses.
   *
   * Debouncing narrows the window but does not close it: a slow reply for an earlier search term
   * can land after a fast reply for the current one and overwrite the list with stale results.
   * Same guard TAActionBoardView already uses for board loads.
   */
  const seqRef = useRef(0);

  const fetchInto = useCallback(
    async (accessToken: string, search?: string) => {
      seqRef.current += 1;
      const seq = seqRef.current;
      try {
        const res = await api.fetchRequests(accessToken, search);
        if (seq !== seqRef.current) return; // a newer query already answered
        applyResponse(res, Boolean(search));
        setExpired(false);
        setError(null); // a success must clear a previous failure, or the tab stays stuck on it
      } catch (e) {
        if (seq !== seqRef.current) return;
        // Firebase does not refresh the Google access token, and a popup opened without a click
        // is blocked by default — so expiry raises a banner with a button rather than reaching
        // for a silent re-auth that would usually fail.
        if (e instanceof api.GmailSessionExpired) {
          setExpired(true);
          void logAnalyticsEvent('ta_requests_gmail_expired', {});
        } else {
          setError(e instanceof Error ? e.message : String(e));
          logAnalyticsError('ta_requests_load', e);
        }
      } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    },
    [applyResponse]
  );

  const load = useCallback(
    async (accessToken: string, search?: string) => {
      setLoading(true);
      setError(null);
      await fetchInto(accessToken, search);
    },
    [fetchInto]
  );

  /**
   * Ask GMAIL when the query changes, debounced.
   *
   * Filtering the loaded list would only ever search the default two-week window, which is the
   * one thing a search must not be limited to: "where is that thread with Marta" is usually about
   * mail older than the work queue shows. So the terms go to Gmail's own full-text search.
   *
   * `searchedRef` holds the terms of the last fetch this effect asked for. Seeded with '' so it does NOT fire on mount, where the query is empty and the effect below is
   * already loading exactly that. Both firing cost a full second visit to Gmail, Lever and the
   * model on every single entry to the tab — and because the mount fetch was then the STALE one,
   * the spinner sat there waiting for the duplicate that started 400ms later.
   */
  const searchedRef = useRef('');

  useEffect(() => {
    if (!gmail.token) return;
    const terms = query.trim();
    if (terms.length > 0 && terms.length < 3) return; // too short to be worth a round trip
    if (terms === searchedRef.current) return; // nothing new to ask
    const timer = setTimeout(() => {
      searchedRef.current = terms;
      if (terms) void logAnalyticsEvent('ta_requests_searched', { term_length: terms.length }); // length only
      void fetchInto(gmail.token as string, terms || undefined);
    }, 400);
    return () => clearTimeout(timer);
  }, [query, gmail.token, fetchInto]);

  /**
   * Load once per mount when a grant already exists.
   *
   * The tab unmounts whenever the recruiter looks at another tab, so without this a cached token
   * would render an empty list instead of the inbox.
   */
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current || !gmail.token) return;
    loadedRef.current = true;
    void fetchInto(gmail.token);
  }, [gmail.token, fetchInto]);

  /** Connect (or re-check) — the only entry point that may show Google's consent screen. */
  const connectAndLoad = useCallback(async () => {
    const accessToken = gmail.token || (await gmail.connect());
    if (accessToken) await load(accessToken);
  }, [gmail, load]);

  const reload = useCallback(() => {
    loadTriggerRef.current = 'manual';
    if (gmail.token) void load(gmail.token);
    void reloadScheduled(); // "Check inbox" should also be right about what has already gone out
  }, [gmail.token, load, reloadScheduled]);

  /** Re-grant after expiry, then immediately refresh — keeps items and the open card in place. */
  const reconnect = useCallback(async () => {
    const fresh = await gmail.connect();
    if (fresh) await load(fresh);
  }, [gmail, load]);

  if (!gmail.connected && items.length === 0) {
    return (
      <GmailConsentGate
        error={gmail.error}
        connecting={gmail.connecting}
        onConnect={() => void connectAndLoad()}
      />
    );
  }

  if (loading && items.length === 0) return <RequestsLoading />;

  // With nothing to show, a failure IS the screen. With the session's queue on screen, it is a
  // banner (below) instead — a background refresh that failed must not throw away a good list.
  if (error && items.length === 0) {
    return <RequestsLoadFailed error={error} onRetry={() => void connectAndLoad()} />;
  }

  if (unavailable) return <RequestsUnavailable reason={unavailable} />;

  /**
   * "Needs you" counts holding replies as well as undrafted ones.
   *
   * Every request now carries something, so counting only `!draft` would report zero work
   * outstanding while a dozen compensation and feedback questions still need a real answer
   * written. A holding reply is prepared, not finished.
   */
  // 'no_reply' is the one undrafted state that is NOT outstanding work: the thread ended in a
  // closing thanks, and counting it would nag recruiters about mails that need nothing. It is

  // The server already answered for these terms, so `items` IS the result set.
  const needle = query.trim().toLowerCase();
  /** Chip filter, then ordering — both from the URL, so neither is lost on the idle reload. */
  const visible = decorated.filter((i) => inBucket(i, bucket));

  // Grouped by role, like Interview feedback and Panel scheduling — see requestGroups.ts.
  const groups = groupByRole(visible, needsAnswer, sort);

  /**
   * Queue the send behind the board's shared countdown. Extracted to
   * `replyAction.ts` like the board's other deferred senders, so this file stays
   * under the line limit.
   */
  const queueSend = (item: InboundRequest, bodyHtml: string) => {
    setBusy(item.threadId, true);
    // The card stays EXPANDED while the countdown runs: collapsing here unmounted
    // the editor, so Stop (or any failure) threw away the recruiter's edited text
    // and rebuilt the card from the server's draft. It closes on success instead.
    deferAction(
      replyAction({
        item,
        bodyHtml,
        gmailToken: gmail.token,
        myEmail,
        onSent: (threadId) => {
          setItems((prev) => {
            const next = prev.filter((i) => i.threadId !== threadId);
            // The badge is lifted to the board, so it has to be told too — otherwise
            // it keeps counting requests that have already been answered.
            onCountChange(next.length);
            return next;
          });
          setBusy(threadId, false);
          setExpanded((cur) => (cur === threadId ? null : cur));
        },
        onExpired: () => {
          setExpired(true);
          setBusy(item.threadId, false);
        },
        onFailed: () => setBusy(item.threadId, false),
        onStopped: () => setBusy(item.threadId, false),
      })
    );
  };

  // Clear one request. `archive` changes only what happens to the MAIL: both record the same triage
  // and both drop the card — our own record, never Gmail, is what stops one coming back.
  const markRead = (item: InboundRequest, { archive = true }: { archive?: boolean } = {}) => {
    setItems((list) => list.filter((i) => i.threadId !== item.threadId));
    onCountChange(items.length - 1);
    setBusy(item.threadId, true);
    void logAnalyticsEvent('ta_requests_marked_read', { category: item.category, archive });
    api
      .dismissRequest(item.threadId, gmail.token, { archive })
      .then((res) => {
        // Only once Gmail-side triage is recorded: on failure the list is restored below, and a
        // cache that had already dropped the thread would hide it on the next visit anyway.
        updateRequestsCache(myEmail, (list) =>
          list.filter((i) => i.threadId !== item.threadId)
        );
        // Every branch means "handled" — only the mailbox differs (dismissOutcome.ts). The proxy's
        // own `archived` wins, and the fallback is TRUE rather than what we asked for: the only
        // proxy that omits the field is one predating the flag, which archives regardless. Falling
        // back to `archive` would promise "stays in your inbox" for a thread that just left it.
        const outcome = dismissOutcome(item.candidateName, res?.gmail, res?.archived ?? true);
        if (outcome.reconnect) setExpired(true);
        showToast({ status: outcome.status, description: outcome.description });
      })
      .catch(() => {
        // Put back only THIS request. Restoring a captured `items` resurrected everything dismissed
        // in the meantime: clear A then B, A fails, and B — already gone from Gmail — comes back as
        // outstanding work. The `some` guard keeps it idempotent if a retry lands twice.
        setItems((list) => {
          const restored = list.some((i) => i.threadId === item.threadId) ? list : [...list, item];
          onCountChange(restored.length);
          return restored;
        });
        showToast({
          status: 'error',
          description: archive
            ? 'Could not archive that request.'
            : 'Could not remove that request from the overview.',
        });
      })
      .finally(() => setBusy(item.threadId, false));
  };

  return (
    <div className="space-y-4">
      {!isOwnBoard && <OtherBoardNotice boardFirstName={boardFirstName} />}
      {expired && (
        <GmailExpiredBanner connecting={gmail.connecting} onReconnect={() => void reconnect()} />
      )}
      {/* The list below is this session's last good read, kept rather than replaced by the error. */}
      {error && <RefreshFailedBanner error={error} retrying={loading} onRetry={reload} />}
      {/* Always the SIGNED-IN viewer's own reminders, on every board view — a non-roster viewer
          never sees "their own" board, and their to-do list must not vanish with the switch. */}
      {/* Above the reminders: a queued reply is the one thing here that acts on its own. */}
      <ScheduledSendsStrip kind="reply" onOpenThread={focusFromReminder} />
      <RemindersStrip onOpenThread={focusFromReminder} />
      <div className="flex flex-wrap items-center gap-2">
        <RequestFilterBar
          total={decorated.length}
          unreadCount={unreadCount}
          bucket={bucket}
          sort={sort}
          onToggleBucket={toggleBucket}
          onSort={setSort}
        />
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, text, or exact email…"
              aria-label="Find a candidate by name"
              className="w-52 rounded-full border border-gray-200 py-1.5 pl-8 pr-3 text-xs text-gray-700 placeholder:text-gray-400 focus:border-gray-300 focus:outline-none"
            />
          </div>
          <Button size="sm" variant="outline" onClick={reload} loading={loading}>
            Check inbox
          </Button>
        </div>
      </div>

      <LeverMatchBanner leverMatch={leverMatch} />

      {visible.length === 0 ? (
        <p className="rounded-lg border p-6 text-sm text-gray-500" style={{ borderColor: '#E3E3E3' }}>
          {!needle
            ? // The bar opens on UNREAD: an empty list usually means "all read", not "nobody wrote".
              bucket !== 'all' && decorated.length > 0
              ? `Nothing ${bucket === 'unread' ? 'unread' : 'in this filter'} — ${decorated.length} request${decorated.length === 1 ? '' : 's'} in the queue. Switch the chip above to see them all.`
              : 'Nothing waiting — no candidate has written in over the last 14 days.'
            : leverMatch
              ? `${leverMatch.name} is in Lever, but has no candidate mail in the last year.`
              : `No candidate mail in the last year matches “${query.trim()}”. Searching an exact email address also looks the candidate up in Lever.`}
        </p>
      ) : (
        <Accordion
          type="multiple"
          // While searching, every group holding a match is forced open: a hit buried in a
          // collapsed accordion is not "found". Same approach FeedbackTab uses for its focus
          // target, and it leaves the user's own toggles untouched once the query is cleared.
          value={needle ? groups.map((g) => g.key) : openRoles}
          onValueChange={setOpenRoles}
          className="space-y-3"
        >
          {groups.map((group) => (
            <RoleAccordion
              key={group.key}
              role={{
                postingId: group.key,
                roleTitle: group.roleTitle,
                hiringManagerName: null,
                candidateCount: group.items.length,
              }}
              hasOpenItems={group.items.length > 0}
              subtitle={`${group.items.length} request${group.items.length === 1 ? '' : 's'} · oldest ${Math.round(group.oldestDays)}d`}
              // At zero this said "0 need you" — a warning about work that does not exist.
              chip={{
                label: group.needsHuman > 0 ? `${group.needsHuman} need you` : 'no review needed',
                bg: group.needsHuman > 0 ? COLORS.orangeBg : COLORS.blueBg,
                text: group.needsHuman > 0 ? COLORS.orangeText : COLORS.blueText,
              }}
            >
              <div className="space-y-3 rounded-lg p-3" style={{ backgroundColor: COLORS.cotton }}>
                {group.items.map((item) => (
                  <InboundCard
                    key={item.threadId}
                    item={item}
                    expanded={expanded === item.threadId}
                    busy={busyIds.has(item.threadId)}
                    onToggle={() => {
                      const opening = expanded !== item.threadId;
                      void logAnalyticsEvent('ta_requests_card_toggled', {
                        expanded: opening,
                        category: item.category,
                        draftSource: item.draftSource,
                      });
                      setExpanded((cur) => (cur === item.threadId ? null : item.threadId));
                    }}
                    onSendReply={(bodyHtml) => queueSend(item, bodyHtml)}
                    onMarkRead={() => markRead(item, { archive: true })}
                    onRemoveFromOverview={() => markRead(item, { archive: false })}
                    onLoadThread={() => loadRequestThread(item, gmail.token, () => setExpired(true))}
                    onCancelInterview={cancelHandlerFor(item)}
                    cancelState={cancelStates[item.threadId] || 'idle'}
                    onLinkSaved={() => void load(gmail.token || '')}
                    gmailToken={gmail.token}
                    scheduleDisabledReason={gmail.token ? null : 'Connect Gmail first, then schedule.'}
                    schedulingConnection={
                      schedulingCtx &&
                      ((tools) => (
                        <RequestSchedulingConnection item={item} ctx={schedulingCtx} replyTools={tools} />
                      ))
                    }
                  />
                ))}
              </div>
            </RoleAccordion>
          ))}
        </Accordion>
      )}
    </div>
  );
}
